import { Link } from "react-router-dom";
import { useState } from "react";
import WaitlistModal from "@/components/WaitlistModal";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
    {
        question: "What is LaunchRadar?",
        answer:
            " LaunchRadar is an intelligence monitoring tool for business, professional, and personal use. It allows users to define project contexts, including sectors, keywords, geographies, and target entities, and receive ranked, summarised signals drawn from publicly available sources on the open web. Signals are delivered via an in-app feed, email digest, and WhatsApp (where enabled).",
    },
    {
        question: "Who is it for?",
        answer:
            "Anyone who needs to stay on top of a specific topic, market, or project without manually checking the news every day, founders, business teams, researchers, or individuals tracking something they care about.",
    },
    {
        question: "How do I use it?",
        answer:
            "Start by signing up, create a project, and tell LaunchRadar what you want to monitor, your sector, keywords, geography, and what \"relevant\" means for your specific context in the form provided. The pipeline runs daily at a time set by you (in the form), scans thousands of sources, and delivers a ranked, summarised feed of signals directly to your dashboard and email(if enabled). The more specific your project setup, the sharper your results.",
    },
    {
        question: "Is it free?",
        answer: (
            <>
                We're currently in a user-testing phase, giving free access to a select
                group of testers. If you think you'd be a good fit for this testing
                period, send us a quick note about your profile and how you'd use
                LaunchRadar at{" "}
                <a
                    href="mailto:info@launchradar.live"
                    className="font-medium text-[#0F1F3D] underline underline-offset-4 hover:text-[#10B981]"
                >
                    info@launchradar.live
                </a>
                . If we introduce paid plans in the future, we'll update our platform
                with pricing and all relevant details.
            </>
        ),
    },
    {
        question: "How does it know what's relevant to me?",
        answer:
            "When you set up a project, you tell us your sectors, keywords, geographic focus, and what ‘relevant’ means in your own words. The more specific you are, the better the results.",
    },
    {
        question: "Where do my results get delivered?",
        answer:
            "You'll find them in your dashboard. You can also opt in to receive them by email (WhatsApp delivery is coming soon).",
    },
    {
        question: "Can the AI get things wrong?",
        answer:
            "Yes. Summaries and relevance scores are AI-generated and may occasionally miss something or misjudge relevance. We recommend always check the original source link below the summary for anything important.",
    },
    {
        question: "Who can see my project information?",
        answer:
            "ORL's platform administrators can see your project names, keywords and result count. We don't sell or share this data with third parties. See our Privacy Policy for full details.",
    }
];

export default function FAQs() {
    const [showWaitlist, setShowWaitlist] = useState(false);

    return (
        <div className="min-h-screen flex flex-col bg-[#F8FAFC] text-[#0F1F3D]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/logo.png"
                            alt="LaunchRadar"
                            className="w-[200px]"
                        />
                    </Link>

                    <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
                        <Link to="/" className="hover:text-[#0F1F3D]">
                            Use cases
                        </Link>

                        <Link to="/" className="hover:text-[#0F1F3D]">
                            How it works
                        </Link>

                        <Link to="/" className="hover:text-[#0F1F3D]">
                            Why us
                        </Link>

                        <Link to="/contact" className="hover:text-[#0F1F3D]">
                            Contact
                        </Link>
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/login"
                            className="text-sm text-[#475569] hover:text-[#0F1F3D]"
                        >
                            Sign in
                        </Link>

                        <button
                            onClick={() => setShowWaitlist(true)}
                            className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="flex-1 max-w-7xl mx-auto w-full px-6 pt-32 pb-20">
                <div className="text-center mb-14">
                    <span className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">
                        Help Center
                    </span>

                    <h1 className="mt-5 text-5xl font-bold tracking-tight">
                        Frequently Asked Questions
                    </h1>

                    <p className="mt-5 max-w-2xl mx-auto text-lg text-[#64748B] leading-8">
                        Find answers to the most common questions about LaunchRadar,
                        monitoring projects, subscriptions, privacy, and how the platform
                        works.
                    </p>
                </div>

                <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm overflow-hidden">
                    <Accordion
                        type="single"
                        collapsible
                        defaultValue="item-0"
                        className="w-full"
                    >
                        {faqs.map((faq, index) => (
                            <AccordionItem
                                key={index}
                                value={`item-${index}`}
                                className="border-[#E2E8F0] px-8"
                            >
                                <AccordionTrigger className="py-6 text-left text-lg font-semibold hover:no-underline">
                                    {faq.question}
                                </AccordionTrigger>

                                <AccordionContent className="pb-6 text-base  text-[#475569]">
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>

                <div className="mt-14 rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#0F1F3D] to-[#172A4F] p-10 text-center text-white">
                    <h2 className="text-3xl font-bold">
                        Still have questions?
                    </h2>

                    <p className="mt-3 text-white/80 max-w-xl mx-auto leading-7">
                        Can't find the answer you're looking for? Our team is happy to help.
                        Reach out and we'll get back to you as soon as possible.
                    </p>

                    <div className="mt-8 flex justify-center gap-4 flex-wrap">
                        <Link
                            to="/contact"
                            className="px-6 py-3 rounded-lg bg-white text-[#0F1F3D] font-semibold hover:bg-[#F8FAFC]"
                        >
                            Contact Us
                        </Link>

                        <button
                            onClick={() => setShowWaitlist(true)}
                            className="px-6 py-3 rounded-lg border border-white/30 hover:bg-white/10"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#E2E8F0] py-10">
                <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-[#64748B]">
                    <p>© 2026 LaunchRadar. All rights reserved.</p>

                    <div className="flex items-center gap-6">
                        <Link
                            to="/privacy-policy"
                            className="hover:text-[#0F1F3D]"
                        >
                            Privacy Policy
                        </Link>

                        <Link
                            to="/terms-and-conditions"
                            className="hover:text-[#0F1F3D]"
                        >
                            Terms & Conditions
                        </Link>

                        <Link
                            to="/faq"
                            className="font-medium text-[#0F1F3D]"
                        >
                            FAQs
                        </Link>

                        <Link
                            to="/contact"
                            className="hover:text-[#0F1F3D]"
                        >
                            Contact
                        </Link>
                    </div>
                </div>
            </footer>

            {showWaitlist && (
                <WaitlistModal
                    onClose={() => setShowWaitlist(false)}
                />
            )}
        </div>
    );
}